import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  MaintenanceRequestFilterValueModel,
  MaintenanceRequestService,
} from '../../../core/services/maintenance-request-service';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { IdNameModel } from '../../../core/models/IdNameModel';
import { Router } from '@angular/router';
import { map } from 'rxjs/internal/operators/map';

@Component({
  selector: 'app-maintenance-request-form',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
  ],
  templateUrl: './maintenance-request-form.html',
  styleUrl: './maintenance-request-form.scss',
})
export class MaintenanceRequestForm implements OnInit {
  today = new Date();
  private fb = inject(FormBuilder);
  private router = inject(Router);

  maintenanceRequestService = inject(MaintenanceRequestService);

  filterValues = this.maintenanceRequestService.maintenanceRequestFilterValues;
  equipmentSearch = new FormControl('');
  locationSearch = new FormControl('');

  filteredEquipmentMachines: IdNameModel[] = [];
  filteredLocations: IdNameModel[] = [];

  selectedFiles: File[] = [];

  imagePreviews: {
    file: File;
    preview: string;
  }[] = [];

  form = this.fb.group({
    equipmentMachineId: [null as number | null],
    locationId: [null as number | null, Validators.required],
    maintenanceCauseId: [null as number | null, Validators.required],
    maintenanceSubCauseId: [null as number | null],
    priority: [null as string | null, Validators.required],
    description: ['', Validators.required],
    isOperational: [true],
    isTaggedOut: [null as boolean | null],
    isLOTOFollowed: [null as boolean | null],
  });

  ngOnInit(): void {
    this.maintenanceRequestService
      .getMaintenanceRequestFilterValues()
      .subscribe({
        next: () => {},
        error: (error) => {
          console.error(
            'Error fetching maintenance request filter values:',
            error,
          );
        },
      });
    this.equipmentSearch.valueChanges.subscribe((value) => {
      const search = typeof value === 'string' ? value.toLowerCase() : '';

      const equipmentMachines = this.filterValues()?.equipmentMachines ?? [];

      this.filteredEquipmentMachines = equipmentMachines.filter((x) =>
        x.name.toLowerCase().includes(search),
      );
    });

    this.locationSearch.valueChanges.subscribe((value) => {
      const search = typeof value === 'string' ? value.toLowerCase() : '';

      const locations = this.filterValues()?.locations ?? [];

      this.filteredLocations = locations.filter((x) =>
        x.name.toLowerCase().includes(search),
      );
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = new FormData();

    const value = this.form.getRawValue();

    Object.entries(value).forEach(([key, val]) => {
      if (val !== null && val !== undefined) {
        formData.append(key, String(val));
      }
    });

    this.selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    this.maintenanceRequestService.createRequest(formData).subscribe({
      next: (response) => {
        const id = response.data;
        this.router.navigate(['/maintenance-request', id]);
      },
    });
  }

  displayEquipment = (item: any): string =>
    item ? item?.name + ' (' + item?.id + ')' : '';

  displayLocation = (item: any): string => item?.name ?? '';

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    Array.from(input.files).forEach((file) => {
      this.selectedFiles.push(file);

      const reader = new FileReader();

      reader.onload = () => {
        this.imagePreviews.push({
          file,
          preview: reader.result as string,
        });
      };

      reader.readAsDataURL(file);
    });

    input.value = '';
  }

  removePhoto(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);
  }
}
